/**
 * ELEGIR LA MÁQUINA O EL APLICADOR DE UNA APLICACIÓN (F2/F3, 26-09-2026).
 *
 * Es lo que abre el «⋯» de crear tarea («Maquinaria…», «Aplicador…») y del
 * registro manual («Cambiar maquinaria»). Una hoja corta con una lista: tocar
 * una línea la elige y cierra. La primera es siempre «Automática»: sin elegir
 * nada, el SERVIDOR pone la de por defecto al completar (applicationContext), y
 * eso es lo normal: el trabajador no ve ningún paso nuevo (T4).
 *
 * Es un FormSheet sin pie: no hay nada que «guardar» aquí, la elección vuelve
 * al formulario que la abrió y se guarda con él.
 */
import type * as React from "react";
import { Check } from "lucide-react";
import { FormSheet } from "@/components/ui/FormSheet";
import { cn } from "@/lib/utils";

export interface ApplicationChoiceOption {
  id: string;
  label: string;
  /** Segunda línea: el nº ROMA, el carné, la finca… */
  detail?: string | null;
  /** Icono a la izquierda (el sol, la nube…). */
  icon?: React.ReactNode;
}

export function ApplicationChoiceSheet({
  open,
  onOpenChange,
  title,
  autoLabel,
  autoDetail,
  options,
  value,
  onChange,
  emptyText,
  testId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /**
   * «Automática» y, debajo, qué saldrá si no se elige nada. Sin él no hay línea
   * «automática» (el tiempo y la eficacia de un parte hecho, F123: se elige uno).
   */
  autoLabel?: string;
  autoDetail?: string | null;
  options: ApplicationChoiceOption[];
  /** `null` = automática. */
  value: string | null;
  onChange: (id: string | null) => void;
  /** Qué decir si no hay nada que elegir (y quién lo da de alta). */
  emptyText?: string;
  testId?: string;
}) {
  const pick = (id: string | null) => {
    onChange(id);
    onOpenChange(false);
  };
  const row = (id: string | null, label: string, detail: string | null | undefined, selected: boolean, icon?: React.ReactNode) => (
    <li key={id ?? "__auto__"}>
      <button
        type="button"
        onClick={() => pick(id)}
        aria-pressed={selected}
        data-testid={testId ? `${testId}-${id ?? "auto"}` : undefined}
        className={cn(
          "w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
          selected ? "border-[#2E84DE] bg-[#2E84DE]/5" : "border-[#e7eef3] bg-white hover:bg-slate-50",
        )}
      >
        {icon ? <span className="shrink-0" aria-hidden>{icon}</span> : null}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground truncate">{label}</div>
          {detail ? <div className="text-xs text-muted-foreground truncate">{detail}</div> : null}
        </div>
        {selected && <Check className="h-4 w-4 shrink-0 text-[#2E84DE]" aria-hidden />}
      </button>
    </li>
  );
  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title={title} fit="auto">
      <ul className="space-y-2" data-testid={testId}>
        {autoLabel != null && row(null, autoLabel, autoDetail, value == null)}
        {options.map((o) => row(o.id, o.label, o.detail, value === o.id, o.icon))}
      </ul>
      {options.length === 0 && emptyText ? <p className="mt-3 text-xs text-muted-foreground">{emptyText}</p> : null}
    </FormSheet>
  );
}
